/**
 * API client factory.
 *
 * Builds a single `@retail-ops/api-client` `ApiClient` instance wired to the
 * environment base URL + function key + Teams SSO (or MSAL fallback) token
 * callback.
 *
 * Per Bible §3.0 rule: function key goes in `x-functions-key` header ONLY,
 * never in the `?code=` query string. The api-client package enforces this.
 */

import { ApiClient } from '@retail-ops/api-client';
import { getTeamsAuthToken } from './teams';
import { getMsalToken } from './msal';

/** Version stamp sent as `x-pcb-client-version`. */
const CLIENT_VERSION = 'ROP-teams@0.1.0';

/** Client id used for every `x-pcb-client-id` header. */
const CLIENT_ID = 'ROP-teams';

export interface ApiClientFactoryOptions {
  baseUrl: string;
  functionKey: string;
  /** If true, use Teams SSO; otherwise MSAL fallback. */
  isInTeamsHost: boolean;
  /** Optional override for the `x-pcb-client-id` header (defaults to `ROP-teams`). */
  clientId?: string;
}

export function createApiClient(options: ApiClientFactoryOptions): ApiClient {
  const clientId = options.clientId ?? CLIENT_ID;
  return new ApiClient({
    baseUrl: options.baseUrl,
    functionKey: options.functionKey,
    version: CLIENT_VERSION,
    getToken: async () => {
      if (options.isInTeamsHost) {
        try {
          return await getTeamsAuthToken();
        } catch {
          // fall through to MSAL
        }
      }
      return getMsalToken();
    },
    context: () => ({ clientId }),
    /**
     * Cold-start retry hook — fires when the api-client transparently
     * retries a transient 5xx (covers the JHA Core Director auth-failed
     * "No such host is known" signature on a fresh Function instance).
     * `ConnectionStatusToast` listens for `pcb:api-retry` and renders a
     * subtle "Reconnecting to Core Director…" banner so the FSR knows
     * something is happening rather than perceiving a frozen UI.
     */
    onRetry: (info) => {
      try {
        window.dispatchEvent(new CustomEvent('pcb:api-retry', { detail: info }));
      } catch {
        // no-op in non-browser contexts
      }
    },
  });
}

/**
 * Build ApiClient options from Vite env vars. Throws in production builds if
 * required env values are missing.
 */
export function apiClientFactoryOptionsFromEnv(isInTeams: boolean): ApiClientFactoryOptions {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  const functionKey = (import.meta.env.VITE_FUNCTION_KEY as string | undefined) ?? '';

  if (!baseUrl && import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error('VITE_API_BASE_URL is not set. The app cannot reach the Function.');
  }

  return {
    baseUrl: baseUrl || 'https://pcb-retail-api.azurewebsites.net/api',
    functionKey,
    isInTeamsHost: isInTeams,
  };
}
